'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactNode } from 'react';
import { MathLib } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, ZERO } from '@morpho-org/dapp-core';
import { HoverableCell, MarketCell } from '@morpho-org/ui-v6/client';
import { CellSimpleText, Donut, ExternalLink, getDonutColor } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Popover, PopoverContent, PopoverTrigger, Separator, Skeleton, Tooltip, TooltipContent, TooltipTrigger, useHasTouchInput } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { type ColumnDef, columnVisibilityFeature, rowSortingFeature, tableFeatures, useTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { formatUnits } from 'viem';
import { InternalLink } from '@/components/common/InternalLink';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { WarningIcon } from '@/components/common/WarningIcon';
import { getMarketUrl } from '@/helpers/routing';
import VaultV2CapDonutSpacer, { CAP_DONUT_RADIUS } from '../../VaultV2Overview/VaultV2Exposure/components/VaultV2CapDonutSpacer';
import { toExposureMarketLabel, type VaultV2ExposureSectionData } from '../../VaultV2Overview/VaultV2Exposure/hooks/useVaultV2ExposureData';
import { getCapUsage, type PublicAllocatorMarketKey, type PublicAllocatorRowState } from './publicAllocator';
function ColumnHeader(t0) {
  const $ = _c(8);
  const {
    label,
    description
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      lineHeight: "normal"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <TooltipTrigger asChild={true}><span className="text-3xs text-secondary-foreground" style={t1}>{label}</span></TooltipTrigger>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = <TooltipContent>{description}</TooltipContent>;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Tooltip disableHoverableContent={false}>{t2}{t3}</Tooltip>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
const detailTooltipTriggerClass = 'inline-flex cursor-help items-center rounded-sm bg-transparent p-0 text-left focus-visible:outline-1 focus-visible:outline-offset-2 hover:bg-transparent active:bg-transparent';
function DetailTooltip(t0) {
  const $ = _c(14);
  const {
    children,
    description
  } = t0;
  const hasTouchInput = useHasTouchInput();
  if (hasTouchInput) {
    let t1;
    if ($[0] !== children) {
      t1 = <PopoverTrigger asChild={true}><button type="button" className={detailTooltipTriggerClass} onClick={_temp}>{children}</button></PopoverTrigger>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== description) {
      t2 = <PopoverContent className="text-2xs max-w-[260px]" onOpenAutoFocus={_temp2} onClick={_temp3}>{description}</PopoverContent>;
      $[2] = description;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1 || $[5] !== t2) {
      t3 = <Popover>{t1}{t2}</Popover>;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  if ($[7] !== children) {
    t1 = <TooltipTrigger asChild={true}><span tabIndex={0} className={detailTooltipTriggerClass}>{children}</span></TooltipTrigger>;
    $[7] = children;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== description) {
    t2 = <TooltipContent>{description}</TooltipContent>;
    $[9] = description;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function _temp3(event_1) {
  return event_1.stopPropagation();
}
function _temp2(event_0) {
  return event_0.preventDefault();
}
function _temp(event) {
  return event.stopPropagation();
}
function formatShortAmount(amount: bigint | undefined, decimals: number, unit?: string) {
  return formatters.short.default('—').trailingZero(false).digits(2).unit(unit).of(amount, decimals).replace(/k/g, 'K');
}
const NO_CAP_TICK_COUNT = 12;
const CAP_DONUT_THICKNESS = 3;
const CAP_DONUT_COLOR = getDonutColor(0);
const NO_ROOM = 0n;
function NoCapDonut() {
  const $ = _c(1);
  const size = CAP_DONUT_RADIUS * 2;
  const innerRadius = CAP_DONUT_RADIUS - CAP_DONUT_THICKNESS / 2;
  const tick = 2 * Math.PI * innerRadius / NO_CAP_TICK_COUNT / 2;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0"><circle cx={CAP_DONUT_RADIUS} cy={CAP_DONUT_RADIUS} r={innerRadius} fill="none" stroke="var(--color-muted)" strokeWidth={CAP_DONUT_THICKNESS} strokeDasharray={`${tick} ${tick}`} /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function HoverCardRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-foreground text-xs">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-foreground text-xs">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between gap-x-4">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function AbsoluteCapCell(t0) {
  const $ = _c(54);
  const {
    absoluteCap,
    allocation,
    asset
  } = t0;
  const isInfinite = absoluteCap >= MathLib.RAY;
  let donutFill;
  let formatAmount;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== absoluteCap || $[1] !== allocation || $[2] !== asset.decimals || $[3] !== asset.symbol || $[4] !== isInfinite) {
    const usage = isInfinite || allocation === undefined ? null : getCapUsage(allocation, absoluteCap);
    let t6;
    if ($[12] !== asset.decimals) {
      t6 = (amount, unit) => formatShortAmount(amount, asset.decimals, unit);
      $[12] = asset.decimals;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    formatAmount = t6;
    donutFill = allocation === undefined || isInfinite ? null : usage === null ? 0 : Math.min(Number(formatUnits(usage, 16)), 100);
    const room = allocation === undefined || isInfinite ? undefined : absoluteCap > allocation ? absoluteCap - allocation : NO_ROOM;
    t1 = "p-sm flex flex-col";
    let t7;
    if ($[14] !== allocation || $[15] !== asset.symbol || $[16] !== formatAmount) {
      t7 = formatAmount(allocation, asset.symbol);
      $[14] = allocation;
      $[15] = asset.symbol;
      $[16] = formatAmount;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    if ($[18] !== t7) {
      t2 = <HoverCardRow label="Allocated" value={t7} />;
      $[18] = t7;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    let t8;
    if ($[20] !== absoluteCap || $[21] !== asset.symbol || $[22] !== formatAmount || $[23] !== isInfinite) {
      t8 = isInfinite ? "No cap" : formatAmount(absoluteCap, asset.symbol);
      $[20] = absoluteCap;
      $[21] = asset.symbol;
      $[22] = formatAmount;
      $[23] = isInfinite;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    if ($[25] !== t8) {
      t3 = <HoverCardRow label="PA cap" value={t8} />;
      $[25] = t8;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    let t9;
    if ($[27] !== asset.symbol || $[28] !== formatAmount || $[29] !== isInfinite || $[30] !== room) {
      t9 = isInfinite ? "No limit" : formatAmount(room, room === NO_ROOM ? undefined : asset.symbol);
      $[27] = asset.symbol;
      $[28] = formatAmount;
      $[29] = isInfinite;
      $[30] = room;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    if ($[32] !== t9) {
      t4 = <HoverCardRow label="Room for PA" value={t9} />;
      $[32] = t9;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    t5 = !isInfinite && <><Separator className="my-xs" />{usage !== null && <HoverCardRow label="Allocation vs PA cap" value={formatters.percent.default("0").trailingZero(false).digits(2).unit("%").of(usage, 18)} />}<p className="text-secondary-foreground text-xs">Vault allocators can allocate past the PA cap. The cap only limits what the Public Allocator can add.</p></>;
    $[0] = absoluteCap;
    $[1] = allocation;
    $[2] = asset.decimals;
    $[3] = asset.symbol;
    $[4] = isInfinite;
    $[5] = donutFill;
    $[6] = formatAmount;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    donutFill = $[5];
    formatAmount = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[34] !== t1 || $[35] !== t2 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5) {
    t6 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[34] = t1;
    $[35] = t2;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  const hoverCardContent = t6;
  let t7;
  if ($[40] !== donutFill || $[41] !== isInfinite) {
    t7 = isInfinite ? <NoCapDonut /> : donutFill === null ? <VaultV2CapDonutSpacer /> : <Donut allocation={donutFill} color={CAP_DONUT_COLOR} backgroundColor="var(--color-muted)" radius={CAP_DONUT_RADIUS} thickness={CAP_DONUT_THICKNESS} />;
    $[40] = donutFill;
    $[41] = isInfinite;
    $[42] = t7;
  } else {
    t7 = $[42];
  }
  let t8;
  if ($[43] !== absoluteCap || $[44] !== allocation || $[45] !== formatAmount || $[46] !== isInfinite) {
    t8 = isInfinite ? <span className="text-2xs text-secondary-foreground" style={{
      lineHeight: "normal"
    }}>No cap</span> : <span className="gap-2xs flex items-baseline" style={{
      lineHeight: "normal"
    }}><span className="text-2xs text-foreground">{formatAmount(allocation)}</span><span className="text-3xs text-secondary-foreground">/ {formatAmount(absoluteCap)}</span></span>;
    $[43] = absoluteCap;
    $[44] = allocation;
    $[45] = formatAmount;
    $[46] = isInfinite;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  let t9;
  if ($[48] !== t7 || $[49] !== t8) {
    t9 = <span className="gap-xs flex items-center">{t7}{t8}</span>;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  let t10;
  if ($[51] !== hoverCardContent || $[52] !== t9) {
    t10 = <HoverableCell hoverCardContent={hoverCardContent} withBackgroundEffect={false} withTouch={true} className="cursor-default">{t9}</HoverableCell>;
    $[51] = hoverCardContent;
    $[52] = t9;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  return t10;
}
function stateDescription(state: PublicAllocatorRowState | undefined) {
  if (state?.status === 'error') return "Couldn't load settings for this Market. Try again later.";
  return "This Market isn't set up for the Public Allocator";
}

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
const publicAllocatorTableFeatures = tableFeatures({
  columnVisibilityFeature,
  rowSortingFeature
});
type PublicAllocatorTableFeatures = typeof publicAllocatorTableFeatures;
export default function PublicAllocatorTable({
  isLoading,
  markets,
  data: section,
  chainId,
  rows,
  canPullFromIdle
}: {
  isLoading: boolean;
  markets: readonly PublicAllocatorMarketKey[];
  data: VaultV2ExposureSectionData;
  chainId: ChainId;
  rows: ReadonlyMap<string, PublicAllocatorRowState>;
  canPullFromIdle: boolean | null;
}) {
  // 'use no memo': `table` keeps one reference while its rows change, so a compiled component
  // would cache the rows of an earlier filter.
  'use no memo';

  const asset = section.asset;
  // The React Compiler also memoizes this, but `data` stays manually memoized as the table input.
  const data = useMemo(() => {
    const positions = section.exposure.markets.map(market => ({
      market: toExposureMarketLabel(chainId, market),
      adapter: market.adapterAddress,
      supplyAssets: BigInt(market.allocatedAssets)
    }));
    return markets.map(key => {
      const position = positions.find(candidate => candidate.market.marketId === key.marketId && candidate.adapter.toLowerCase() === key.adapter.toLowerCase());
      return {
        ...key,
        market: position?.market,
        supplyAssets: position?.supplyAssets ?? ZERO,
        state: rows.get(`${key.marketId}:${key.adapter.toLowerCase()}`)
      };
    }).toSorted((a, b) => a.supplyAssets === b.supplyAssets ? 0 : a.supplyAssets > b.supplyAssets ? -1 : 1);
  }, [section, chainId, markets, rows]);
  const needsAdapterColumn = data.some((row, index) => data.some((other, otherIndex) => index !== otherIndex && row.marketId === other.marketId && row.adapter.toLowerCase() !== other.adapter.toLowerCase()));
  const columns: ColumnDef<PublicAllocatorTableFeatures, (typeof data)[number]>[] = [{
    id: 'market',
    header: () => <ColumnHeader label="Markets" description="Markets in this Vault, plus its idle assets" />,
    cell: ({
      row: row_0
    }) => {
      const market_0 = row_0.original.market;
      if (!market_0) return <CellSimpleText>Unavailable</CellSimpleText>;
      const content = <MarketCell collateralAsset={market_0.collateralAsset} loanAsset={market_0.loanAsset} lltv={market_0.lltv} renderWarnings={<WarningIcon warnings={market_0.warnings} />} />;
      return market_0.collateralAsset && market_0.loanAsset ? <InternalLink href={getMarketUrl({
        identifier: InterfaceChainUtils.getIdentifierById(chainId),
        marketId: market_0.marketId,
        collateralSymbol: market_0.collateralAsset.symbol,
        loanSymbol: market_0.loanAsset.symbol
      })}>
            {content}
          </InternalLink> : content;
    }
  }, {
    id: 'adapter',
    header: () => <ColumnHeader label="Adapter" description={<>
              The adapter this Vault uses to allocate to this Market.{' '}
              <ExternalLink href="https://docs.morpho.org/learn/concepts/vault-v2/" className="text-primary underline underline-offset-2">
                Learn more
              </ExternalLink>
            </>} />,
    cell: ({
      row: row_1
    }) => <DetailTooltip description={row_1.original.adapter}>
          <CellSimpleText>{abbreviateAddress(row_1.original.adapter)}</CellSimpleText>
        </DetailTooltip>
  }, {
    id: 'canPullFromMarket',
    header: () => <ColumnHeader label="Pull from market" description="Whether this Market's assets can be moved to other Markets in this Vault" />,
    cell: ({
      row: row_2
    }) => {
      const state = row_2.original.state;
      if (state?.status === 'loading') return <Skeleton className="h-4 w-16" />;
      return <DetailTooltip description={state?.status === 'active' ? state.candidate.canPullFromMarket ? 'Assets in this Market can be moved to other Markets in this Vault, within their limits' : "Assets in this Market can't be moved to other Markets. This doesn't affect your withdrawals." : stateDescription(state)}>
            <CellSimpleText>
              {state?.status === 'active' ? state.candidate.canPullFromMarket ? 'Allowed' : 'Not allowed' : state?.status === 'inactive' ? 'Not active' : 'Unavailable'}
            </CellSimpleText>
          </DetailTooltip>;
    }
  }, {
    id: 'absoluteCap',
    header: () => <ColumnHeader label={`Public allocator cap (${asset?.symbol ?? ''})`} description={<>
              The Public Allocator stops moving assets into this Market once the Vault&apos;s
              allocation reaches this amount. The chart shows the current allocation against it.{' '}
              <ExternalLink href="https://docs.morpho.org/learn/concepts/vault-v2/#caps-and-risk-controls" className="text-primary underline underline-offset-2">
                Vault caps
              </ExternalLink>{' '}
              still apply.
            </>} />,
    cell: ({
      row: row_3
    }) => {
      const state_0 = row_3.original.state;
      if (state_0?.status === 'loading') return <Skeleton className="h-4 w-16" />;
      if (state_0?.status !== 'active') {
        return <DetailTooltip description={stateDescription(state_0)}>
              <CellSimpleText>—</CellSimpleText>
            </DetailTooltip>;
      }
      const {
        absoluteCap
      } = state_0.candidate;
      if (!asset) return <CellSimpleText>—</CellSimpleText>;
      return <AbsoluteCapCell absoluteCap={BigInt(absoluteCap)} allocation={row_3.original.supplyAssets} asset={asset} />;
    }
  }];
  const table = useTable({
    features: publicAllocatorTableFeatures,
    data,
    columns,
    getRowId: row_4 => `${row_4.marketId}:${row_4.adapter}`,
    enableSorting: false,
    state: {
      columnVisibility: {
        adapter: needsAdapterColumn
      }
    }
  });
  return <div className="gap-sm flex min-w-0 flex-col">
      <TableScrollArea className="grid h-full w-full rounded-[20px]">
        <Table.Table tableVariant="compact" className="[&_.td-content]:pr-[16px] [&_th]:pr-[16px]">
          <caption className="sr-only">Public Allocator settings by Market and idle assets</caption>
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />)}
          </Table.Thead>
          <Table.Tbody>
            {table.getRowModel().rows.map(row_5 => <TrBodyRow key={row_5.id} row={row_5} />)}
            <Table.Tr>
              <Table.Td>
                <DetailTooltip description="Assets the Vault holds but hasn't allocated to any Market">
                  <CellSimpleText>Idle assets</CellSimpleText>
                </DetailTooltip>
              </Table.Td>
              {needsAdapterColumn && <Table.Td>
                  <CellSimpleText>—</CellSimpleText>
                </Table.Td>}
              <Table.Td>
                {isLoading ? <Skeleton className="h-4 w-16" /> : <DetailTooltip description={canPullFromIdle === null ? "Idle assets aren't set up for the Public Allocator" : canPullFromIdle ? 'Idle assets can be moved into Markets in this Vault' : "Idle assets can't be moved into Markets. This doesn't affect your withdrawals."}>
                    <CellSimpleText>
                      {canPullFromIdle === null ? 'Unavailable' : canPullFromIdle ? 'Allowed' : 'Not allowed'}
                    </CellSimpleText>
                  </DetailTooltip>}
              </Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tbody>
        </Table.Table>
      </TableScrollArea>
    </div>;
}